import { logger } from '../utilities/logger';
import { Native } from '../utilities/native';
import { Auth } from '../utilities/auth';

/**
 * Config Service helper class
 *
 */

enum ENVS {
  QA = 'qa',
  STAGING = 'staging',
  PENTEST = 'pentest',
  PRODUCTION = 'production',
}

enum HOSTED_ZONE {
  DEV = 'idbs-dev.com',
  STAGING = 'idbs-staging.com',
  CLOUD = 'idbs-cloud.com',
}

/*
qa is on idbs-dev
staging is on idbs-staging
pentest is on idbs-staging
production is on idbs-cloud
*/

class ConfigService {
  static #configServiceUrl: string = '';

  /**
   * Forms the config service URL by getting the tenant, environment,
   * and hosted zone configuration from the current URL
   */
  static formConfigServiceUrl(url: URL): string {
    if (import.meta.env.VITE_CONFIG_SERVICE_URL) {
      return import.meta.env.VITE_CONFIG_SERVICE_URL;
    }

    const urlParts = url.hostname.split('.');

    let tenant = url.pathname.split('/')[1];
    const appName = urlParts[0];

    const zone = urlParts.slice(1).join('.');

    // can override tenant with env var
    if (import.meta.env.VITE_TENANT) {
      tenant = import.meta.env.VITE_TENANT;
    }

    let env: ENVS = ENVS.QA;

    if (zone.includes(HOSTED_ZONE.DEV)) {
      env = ENVS.QA;
    } else if (appName.includes('pentest')) {
      // pentest env is also in idbs-staging so can't rely on the zone
      env = ENVS.PENTEST;
    } else if (zone.includes(HOSTED_ZONE.STAGING)) {
      env = ENVS.STAGING;
    } else if (zone.includes(HOSTED_ZONE.CLOUD)) {
      env = ENVS.PRODUCTION;
    }
    this.#configServiceUrl = `https://${tenant}.config-service-${env}.${zone}/v1/query`;
    logger.info(`Config service URL: ${this.#configServiceUrl}`);
    return this.#configServiceUrl;
  }

  /**
   * Constructs the config service URL based on the current href.
   * @returns the config service URL
   */
  static buildConfigServiceUrl = () => {
    // If we are running in localhost we don't need to set the config service URL
    if (Native.getHostname() === 'localhost') {
      logger.info('Running in localhost - no config service URL required');
      return 'localhost/v1/query';
    }

    // Otherwise we need to set the config service URL based on the href
    return this.formConfigServiceUrl(new URL(Native.getHref()));
  };

  /**
   * Helper function for sending requests to the config service.
   * @param query the query to send to the config service
   * @returns json object containing the data and any errors
   */
  static configServiceRequest = async (query: string) => {
    const configServiceQueryUrl = this.buildConfigServiceUrl();
    const response = await fetch(configServiceQueryUrl, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${Auth.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
      }),
    });

    if (response.ok) {
      const json = await response.json();
      return { data: json.data, errors: json.errors };
    }

    return { data: null, errors: [{ message: response.statusText }] };
  };

  static get configServiceUrl() {
    return this.#configServiceUrl;
  }
}

export { ConfigService };
